import {Pagination} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useRef} from 'react';
import styles from './tableTopScrollbar.module.scss';

/**
 * The pager and a mirrored horizontal scrollbar, pinned above a wide table.
 *
 * Both controls used to live only under the last row. On a hundred-row page
 * that put them several screens away: changing page meant scrolling to the
 * bottom and back, and scrolling sideways from the middle of the list was
 * impossible without a trackpad gesture or shift-wheel, because neither
 * scrollbar was on screen. Pinning them here keeps both reachable from
 * anywhere in the list.
 *
 * The two are deliberately one sticky element rather than two stacked ones —
 * stacking would mean offsetting the second by the measured height of the
 * first, which breaks the moment the pager wraps to two lines.
 *
 * Render it as a sibling *before* the element that wraps the table, never
 * inside it: those wrappers carry `overflow: hidden` for their rounded corners
 * or `overflow-x` for the table's own scrolling, and an overflowing ancestor
 * becomes the sticky containing block — the bar would render correctly and
 * then silently never stick.
 */
const TableTopScrollbar = ({pagerProps}) => {
  const barRef = useRef(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;

    const spacer = bar.firstElementChild;

    /**
     * The element that actually scrolls sideways.
     *
     * Found by measurement rather than by class name, because these tables
     * scroll three different ways: antd's `.ant-table-content`, its
     * `.ant-table-body` (when the table has a fixed header), and on some
     * pages a plain wrapper div carrying `overflow-x` from the page's own
     * stylesheet. Measuring covers all three and survives a table moving
     * between them.
     *
     * Walks up from the bar because the bar sits outside the table's wrapper
     * — it has to, to escape that wrapper's overflow — so the table is not
     * among its own parent's descendants but its grandparent's. The walk is
     * capped: the table is one or two levels up by construction, and letting
     * it reach <body> turned every sync into a scan of the whole document.
     */
    const MAX_LEVELS = 4;

    const findScroller = () => {
      let scope = bar.parentElement;
      for (let level = 0; scope && level < MAX_LEVELS; level += 1) {
        const hit = [...scope.querySelectorAll('*')].find(
          node =>
            node !== bar &&
            !bar.contains(node) &&
            node.scrollWidth > node.clientWidth + 1 &&
            /auto|scroll/.test(getComputedStyle(node).overflowX),
        );
        if (hit) return hit;
        scope = scope.parentElement;
      }
      return undefined;
    };

    let scroller = findScroller();

    /**
     * The cached scroller, re-resolved only when it is actually stale.
     *
     * antd replaces its inner nodes on a re-render, so the cache cannot be
     * permanent — but re-running the search on every sync meant a full
     * subtree scan on every resize and every frame of a scroll, which is what
     * made a wide table feel like it was locking up.
     */
    const currentScroller = () => {
      if (
        scroller &&
        scroller.isConnected &&
        scroller.scrollWidth > scroller.clientWidth + 1
      ) {
        return scroller;
      }
      scroller = findScroller();
      return scroller;
    };

    const sync = () => {
      const target = currentScroller();
      if (!target) {
        // No stray bar above a table that already fits.
        bar.classList.add(styles.hidden);
        return;
      }
      bar.classList.remove(styles.hidden);
      // Only write when the value actually changed: this runs from a
      // ResizeObserver, and an unconditional style write there re-triggers
      // the observer on the next frame for as long as the page is open.
      const width = `${target.scrollWidth}px`;
      if (spacer.style.width !== width) spacer.style.width = width;
      if (bar.scrollLeft !== target.scrollLeft) {
        bar.scrollLeft = target.scrollLeft;
      }
    };

    // A flag rather than detaching listeners: setting scrollLeft fires the
    // other element's scroll event, which would set this one back and leave
    // the two juddering against each other.
    let syncing = false;
    const onBar = () => {
      const target = currentScroller();
      if (syncing || !target) return;
      syncing = true;
      target.scrollLeft = bar.scrollLeft;
      syncing = false;
    };
    const onContent = event => {
      // Compared against the cached node rather than re-resolving: this fires
      // for every scroll on the page, including the window's own.
      if (syncing || !scroller || event.target !== scroller) return;
      syncing = true;
      bar.scrollLeft = scroller.scrollLeft;
      syncing = false;
    };

    sync();
    bar.addEventListener('scroll', onBar);
    // Captured at the document: the scroller is replaced on re-render, so a
    // listener bound straight to it would be left on a discarded node.
    document.addEventListener('scroll', onContent, true);

    // eslint-disable-next-line no-undef
    const observer = new ResizeObserver(sync);
    if (bar.parentElement) observer.observe(bar.parentElement);

    // Column widths settle a frame after the rows paint, so the first measure
    // can read a width that is about to change.
    const settle = setTimeout(sync, 300);

    return () => {
      clearTimeout(settle);
      observer.disconnect();
      bar.removeEventListener('scroll', onBar);
      document.removeEventListener('scroll', onContent, true);
    };
  });

  return (
    <div className={styles.sticky}>
      {pagerProps && (
        <Pagination
          {...pagerProps}
          className="ant-table-pagination ant-table-pagination-top"
        />
      )}
      <div ref={barRef} className={styles.bar} aria-hidden="true">
        <div className={styles.spacer} />
      </div>
    </div>
  );
};

TableTopScrollbar.propTypes = {
  // Omit to render the scrollbar on its own, for a table with no pager.
  pagerProps: PropTypes.object,
};

export default TableTopScrollbar;
